import { SupportAgentSkills } from "@redotech/redo-agents-cards/support-agents/skills";
import { BillingStatus } from "@redotech/redo-concierge-conversation-common/concierge-conversation-message-definition";
import { conciergeConversationMessageSchema } from "@redotech/redo-concierge-conversation-common/rpc/message";
import { contextSchema } from "@redotech/redo-concierge-conversation-common/rpc/product-details";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { array, date, object, string, enum as zEnum } from "zod";
import { ConversationMessageType } from "../../conversation";
import { ConversationMessageDbParser } from "../../conversation/conversation-message-db-parser";
import { ConversationSchemaDbParser } from "../../conversation/conversation-schema-db-parser";
export const analyticsTableSchema = z.object({
  pdpInteractions: z
    .array(
      z.object({
        productDetails: z.object({
          productId: z.string(),
          productName: z.string(),
          imgSrc: z.string().url().nullable(),
        }),
      }),
    )
    .optional(),
});

export const conciergeConversationSchema = object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  email: string().optional(),
  context: contextSchema.optional(),
  messages: array(conciergeConversationMessageSchema),
  lastMessageCreatedAt: date(),
  createdAt: date(),
  updatedAt: date(),
  billingStatus: zEnum(BillingStatus).optional(),
  categories: array(string()).optional(),
  /**
   * @deprecated
   */
  convertedToSupportConversation: z.boolean().optional(),
  convertedToSupport: z
    .object({
      converted: z.boolean(),
      supportConversationId: zExt.objectId().optional(),
    })
    .optional(),
  analytics: analyticsTableSchema.optional(),
  orders: array(zExt.objectId()).optional(),
});

export type ConciergeConversation = z.infer<typeof conciergeConversationSchema>;

export const CurrentProductInfoSchema = z.object({
  productId: z.string(),
  productName: z.string(),
  variantId: z.string().optional(),
  storefrontProductUrl: z.string().optional(),
});
export type CurrentProductInfo = z.infer<typeof CurrentProductInfoSchema>;

const quickResponseSchema_ = z.object({
  text: z.string(),
  shouldSend: z.boolean(),
});
export const QuickResponseSchema: z.ZodType<QuickResponse> =
  quickResponseSchema_;
export interface QuickResponse extends z.infer<typeof quickResponseSchema_> {}

const InitialMessageSchema = z.object({
  type: createEnum(ConversationMessageType),
  content: z.string(),
});

export interface GetConciergeResponseDef {
  name: "getConciergeResponse";
  input: z.ZodObject<{
    conversationId: z.ZodOptional<z.ZodString>;
    initialMessages: z.ZodOptional<z.ZodArray<typeof InitialMessageSchema>>;
    message: z.ZodOptional<z.ZodString>;
    customerTimezone: z.ZodOptional<z.ZodString>;
    currentProductInfo: z.ZodOptional<typeof CurrentProductInfoSchema>;
    activeCartId: z.ZodOptional<z.ZodString>;
    platform: z.ZodOptional<z.ZodEnum<typeof ConversationPlatform>>;
    returnRefreshedConversation: z.ZodOptional<z.ZodBoolean>;
    isPdpSource: z.ZodOptional<z.ZodBoolean>;
    generateQuickResponses: z.ZodOptional<z.ZodBoolean>;
  }>;
  output: typeof baseMessageResponseSchema;
}

export type InitialMessage = z.infer<typeof InitialMessageSchema>;

// Unified configuration object for messaging endpoints
const messagingConfigSchema = z.object({
  // Authentication tokens
  token: z.string().optional(),
  customerAuthToken: z.string().optional(),

  // Chat flow state
  currentChatFlowStep: z.number().nullable().optional(),

  // Collection flags
  collectEmail: z.boolean().optional(),
  fourDigitCodeId: z.string().optional(),

  // Conversion flags
  offeringConvertToEmail: z.boolean().optional(),

  // Additional identifiers
  teamId: z.string().optional(),
  conversationId: z.string().optional(),
  email: z.string().optional(),
});

export type MessagingConfig = z.infer<typeof messagingConfigSchema>;

// Base response schema (for backwards compatibility with getConciergeResponse)
export const baseMessageResponseSchema = z.object({
  messages: z.array(ConversationMessageDbParser),
  newConversationBeforeResponse: ConversationSchemaDbParser.nullable(),
  quickResponses: z.array(quickResponseSchema_).optional(),
});

// Extended unified response schema (includes config for new endpoints)
const unifiedMessageResponseSchema_ = z.object({
  messages: z.array(ConversationMessageDbParser),
  newConversationBeforeResponse: ConversationSchemaDbParser.nullable(),
  config: messagingConfigSchema.optional(),
  quickResponses: z.array(quickResponseSchema_).optional(),
});

export const unifiedMessageResponseSchema: z.ZodType<UnifiedMessageResponse> =
  unifiedMessageResponseSchema_;
export interface UnifiedMessageResponse extends z.infer<
  typeof unifiedMessageResponseSchema_
> {}
export const getResponseSchema: GetConciergeResponseDef = {
  name: "getConciergeResponse",
  input: z
    .object({
      conversationId: z.string().optional(),
      initialMessages: z.array(InitialMessageSchema).optional(),
      message: z.string().optional(),
      customerTimezone: z.string().optional(),
      currentProductInfo: CurrentProductInfoSchema.optional(),
      activeCartId: z.string().optional(),
      platform: createEnum(ConversationPlatform).optional(),
      returnRefreshedConversation: z.boolean().optional(),
      isPdpSource: z.boolean().optional(),
      generateQuickResponses: z.boolean().optional(),
    })
    .strict(),

  output: baseMessageResponseSchema,
};

export type GetResponseInput = z.infer<typeof getResponseSchema.input>;
export type GetResponseOutput = z.infer<typeof getResponseSchema.output>;

export const conciergeConversationListItemSchema = object({
  _id: zExt.objectId(),
  recentMessageText: string(),
  lastMessageCreatedAt: date(),
  assignee: zExt.objectId().optional(),
  platform: string().optional(),
});

export type ConciergeConversationListItem = z.infer<
  typeof conciergeConversationListItemSchema
>;

export function isConciergeConversationListItem(
  chatInfo: any,
): chatInfo is ConciergeConversationListItem {
  return conciergeConversationListItemSchema.safeParse(chatInfo).success;
}

export function getDefaultQuickResponses(
  skills: SupportAgentSkills,
): QuickResponse[] {
  const quickResponses: QuickResponse[] = [];

  // Define quick responses for each skill, ordered by "interest" level
  const skillQuickResponses = [
    // Most interesting/useful skills first
    { skill: skills.updateOrder, text: "Update my order" },
    { skill: skills.cancelOrder, text: "Cancel my order" },
    {
      skill: { enabled: true },
      text: "How does this compare to similar products?",
    },
    { skill: skills.changeAddress, text: "Change shipping address" },
    { skill: skills.orderTracking, text: "Track my order" },
    {
      skill: skills.backInStockNotifications,
      text: "Notify when back in stock",
    },
    { skill: skills.cartEditing, text: "Modify my cart" },
    { skill: skills.productBundle, text: "Any bundle deals?" },
    { skill: skills.createDiscountCode, text: "Any discount codes?" },
  ];

  // Add enabled skills to quick responses (limit to 3)
  for (const skillResponse of skillQuickResponses) {
    if (skillResponse.skill?.enabled && quickResponses.length < 3) {
      quickResponses.push({ text: skillResponse.text, shouldSend: true });
    }
  }

  return quickResponses;
}
